import {
  addClassNamesToElement,
  removeClassNamesFromElement,
} from '@lexical/utils';
import { $generateHtmlFromNodes } from '@lexical/html';
import {
  $applyNodeReplacement,
  createEditor,
  DecoratorNode,
  isHTMLElement,
} from 'lexical';
import * as React from 'react';
import InlineImageComponent from './InlineImageComponent';

/**
 * Parse a CSS length value (e.g. "6.268in", "451.5pt", "16cm", "602px")
 * and return an integer pixel value, or null if it can't be parsed.
 */
function parseCSSLengthToPixels(value) {
  if (!value || value === 'auto' || value === 'inherit' || value === 'initial') return null;
  const num = parseFloat(value);
  if (isNaN(num) || num <= 0) return null;
  if (value.endsWith('in')) return Math.round(num * 96);
  if (value.endsWith('pt')) return Math.round(num * (96 / 72));
  if (value.endsWith('cm')) return Math.round(num * (96 / 2.54));
  if (value.endsWith('mm')) return Math.round(num * (96 / 25.4));
  if (value.endsWith('px')) return Math.round(num);
  // Percentage / em / rem – don't convert, they're relative
  if (value.endsWith('%') || value.endsWith('em') || value.endsWith('rem')) return null;
  // Plain number – treat as pixels
  if (!isNaN(Number(value))) return Math.round(num);
  return null;
}

function $convertInlineImageElement(domNode) {
  if (isHTMLElement(domNode) && domNode.nodeName === 'IMG') {
    const { alt: altText, src } = domNode;

    // Read width/height from HTML attributes first (integer or 0)
    let width = domNode.width;
    let height = domNode.height;

    // If HTML attributes are missing (0), try inline style
    // Word often uses: style="width:6.268in;height:4.696in"
    if (!width && domNode.style && domNode.style.width) {
      width = parseCSSLengthToPixels(domNode.style.width);
    }
    if (!height && domNode.style && domNode.style.height) {
      height = parseCSSLengthToPixels(domNode.style.height);
    }

    // Also try the raw attribute string as fallback (handles "602" without unit)
    if (!width) {
      const attrW = domNode.getAttribute('width');
      if (attrW) width = parseCSSLengthToPixels(attrW);
    }
    if (!height) {
      const attrH = domNode.getAttribute('height');
      if (attrH) height = parseCSSLengthToPixels(attrH);
    }

    // Get position from the position attribute or extract from class
    let position = domNode.getAttribute('position');
    let captionAttr = domNode.getAttribute('caption');
    let showCaption = domNode.getAttribute('showCaption');

    // Convert showCaption string to boolean
    showCaption = showCaption === 'true' || showCaption === true;

    if (!position) {
      // Fallback: extract position from class names like "position-right"
      const classList = domNode.className.split(' ');
      const positionClass = classList.find(cls => cls.startsWith('position-'));
      if (positionClass) {
        position = positionClass.replace('position-', '');
      }
    }

    // Default to 'left' (50% width, like the upload dialog) when no position is found
    if (!position) {
      position = 'left';
    }

    // Create a new editor for caption
    let caption = createEditor();

    // If we have caption data, try to parse and set it
    if (captionAttr) {
      try {
        // The caption attribute contains HTML-encoded JSON, so decode it first
        const decodedCaption = captionAttr.replace(/&quot;/g, '"');
        const captionData = JSON.parse(decodedCaption);

        // Parse the editor state and set it
        if (captionData.editorState) {
          const editorState = caption.parseEditorState(captionData.editorState);
          // Only set editor state if it's not empty (prevents "editor state is empty" error)
          if (!editorState.isEmpty()) {
            caption.setEditorState(editorState);
          }
        }
      } catch (e) {
        console.warn('Failed to parse caption data:', e);
        // Keep the empty editor if parsing fails
      }
    }

    const node = $createInlineImageNode({
      altText,
      src,
      width,
      height,
      position,
      showCaption,
      caption
    });
    return { node };
  }
  return null;
}

function getPositionClass(position) {
  return typeof position === 'string' ? `position-${position}` : undefined;
}

export class InlineImageNode extends DecoratorNode {
  __src;
  __altText;
  __width;
  __height;
  __showCaption;
  __caption;
  __position;

  static getType() {
    return 'inline-image';
  }

  static clone(node) {
    return new InlineImageNode(
      node.__src,
      node.__altText,
      node.__position,
      node.__width,
      node.__height,
      node.__showCaption,
      node.__caption,
      node.__key,
    );
  }

  static importJSON(serializedNode) {
    const { altText, height, width, src, showCaption, position } = serializedNode;
    return $createInlineImageNode({
      altText,
      height,
      position,
      showCaption,
      src,
      width,
    }).updateFromJSON(serializedNode);
  }

  updateFromJSON(serializedNode) {
    const { caption } = serializedNode;
    const node = super.updateFromJSON(serializedNode);
    const nestedEditor = node.__caption;
    const editorState = nestedEditor.parseEditorState(caption.editorState);
    if (!editorState.isEmpty()) {
      nestedEditor.setEditorState(editorState);
    }
    return node;
  }

  static importDOM() {
    return {
      img: (node) => ({
        conversion: $convertInlineImageElement,
        priority: 2,
      }),
    };
  }

  constructor(
    src,
    altText,
    position,
    width,
    height,
    showCaption,
    caption,
    key,
  ) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width || 'auto';
    this.__height = height || 'auto';
    this.__showCaption = showCaption || false;
    this.__caption = caption || createEditor();
    this.__position = position;
  }

  exportDOM() {
    // Create the wrapper span
    const wrapper = document.createElement('span');

    // Add classes - make sure to include both the base class and position class
    const classes = ['inline-editor-image'];
    const positionClass = getPositionClass(this.__position);
    if (positionClass) {
      classes.push(positionClass);
    }
    wrapper.className = classes.join(' ');

    // Only set position attribute if needed for your CSS
    if (this.__position) {
      wrapper.setAttribute('data-position', this.__position);
    }

    // Create the img
    const img = document.createElement('img');
    img.setAttribute('src', this.__src);
    img.setAttribute('alt', this.__altText);
    img.setAttribute('width', this.__width.toString());
    img.setAttribute('height', this.__height.toString());

    // Set position attribute
    if (this.__position) {
      img.setAttribute('position', this.__position);
    }

    // Set showCaption as boolean string
    img.setAttribute('showCaption', this.__showCaption.toString());

    // Serialize caption properly - convert editor state to JSON string
    if (this.__caption) {
      try {
        const captionJSON = JSON.stringify(this.__caption.toJSON());
        img.setAttribute('caption', captionJSON);
      } catch (e) {
        console.warn('Failed to serialize caption:', e);
        img.setAttribute('caption', '');
      }
    }

    img.className = positionClass || '';
    img.style.display = 'block';
    img.style.paddingBottom = "20px";

    // Append img to wrapper
    wrapper.appendChild(img);

    return { element: wrapper };
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      altText: this.getAltText(),
      caption: this.__caption.toJSON(),
      height: this.__height === 'inherit' ? 0 : this.__height,
      position: this.__position,
      showCaption: this.__showCaption,
      src: this.getSrc(),
      width: this.__width === 'inherit' ? 0 : this.__width,
    };
  }

  getSrc() {
    return this.__src;
  }

  getAltText() {
    return this.__altText;
  }

  setAltText(altText) {
    const writable = this.getWritable();
    writable.__altText = altText;
  }

  setWidthAndHeight(width, height) {
    const writable = this.getWritable();
    writable.__width = width;
    writable.__height = height;
  }

  getShowCaption() {
    return this.__showCaption;
  }

  setShowCaption(showCaption) {
    const writable = this.getWritable();
    writable.__showCaption = showCaption;
  }

  getCaption() {
    return this.__caption;
  }

  setCaption(caption) {
    const writable = this.getWritable();
    writable.__caption = caption;
  }

  getPosition() {
    return this.__position;
  }

  setPosition(position) {
    const writable = this.getWritable();
    writable.__position = position;
  }

  update(payload) {
    const writable = this.getWritable();
    const { altText, showCaption, position } = payload;
    if (altText !== undefined) {
      writable.__altText = altText;
    }
    if (showCaption !== undefined) {
      writable.__showCaption = showCaption;
    }
    if (position !== undefined) {
      writable.__position = position;
    }
  }

  // View
  createDOM(config) {
    const span = document.createElement('span');

    // Always add the base inline-editor-image class
    span.className = 'inline-editor-image';

    // Add theme class if available
    if (config.theme.inlineImage) {
      addClassNamesToElement(span, config.theme.inlineImage);
    }

    // Add position class
    const positionClass = getPositionClass(this.__position);
    if (positionClass) {
      addClassNamesToElement(span, positionClass);
    }

    return span;
  }

  updateDOM(prevNode, dom, config) {
    const position = this.__position;
    const prevPosition = prevNode.__position;
    let domUpdated = false;

    if (position !== prevPosition) {
      // Update wrapper classes
      const prevPositionClass = getPositionClass(prevPosition);
      if (prevPositionClass) {
        removeClassNamesFromElement(dom, prevPositionClass);
        domUpdated = true;
      }

      const newPositionClass = getPositionClass(position);
      if (newPositionClass) {
        addClassNamesToElement(dom, newPositionClass);
        domUpdated = true;
      }

      // Update the inner image element as well
      const imgElement = dom.querySelector('img');
      if (imgElement) {
        // Update image position attribute
        if (position) {
          imgElement.setAttribute('position', position);
        } else {
          imgElement.removeAttribute('position');
        }

        // Update image class
        imgElement.className = newPositionClass || '';
        domUpdated = true;
      }
    }
    return domUpdated;
  }

  decorate() {
    return (
      <InlineImageComponent
        src={this.__src}
        altText={this.__altText}
        width={this.__width}
        height={this.__height}
        nodeKey={this.getKey()}
        showCaption={this.__showCaption}
        caption={this.__caption}
        position={this.__position}
      />
    );
  }
}

export function $createInlineImageNode({
  altText,
  position,
  height,
  src,
  width,
  showCaption,
  caption,
  key,
}) {
  return $applyNodeReplacement(
    new InlineImageNode(
      src,
      altText,
      position,
      width,
      height,
      showCaption,
      caption,
      key,
    ),
  );
}

export function $isInlineImageNode(node) {
  return node instanceof InlineImageNode;
}